import { useMutation } from "@apollo/client";
import Check from "@mui/icons-material/Check";
import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import {
  Box,
  IconButton,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { GridActionsCellItem } from "@mui/x-data-grid-premium";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { TextCellWithTooltip } from "components/DataGridCell";
import { ExportDataButton } from "components/ExportDataButton";
import { IssueCount } from "components/IssueCount";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import {
  DocumentType,
  PureOrMixture,
  TenantCatalogChemicalFragment,
  DensityUnits,
} from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useCurrentUser } from "hooks/useCurrentUser";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useTenant } from "hooks/useTenant";
import React, { useCallback, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getChemicalsBreadcrumb,
  getChemicalsCatalogBreadcrumb,
} from "util/breadcrumb";
import { formatChemicalName } from "util/chemicalName";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { AddChemicalButton } from "./AddChemicalButton";
import { ChemicalCatalogBulkActions } from "./ChemicalCatalogBulkActions";
import { StaffBulkActionsMenu } from "./StaffBulkActionsMenu";
import { DELETE_CHEMICAL, TENANT_CATALOG_CHEMICALS } from "./schema";

type Row = TenantCatalogChemicalFragment;

export const TenantChemicalsTable: React.FC = () => {
  const navigate = useNavigate();
  const { tenantId } = useParams<{ tenantId: string }>();
  const { tenant } = useTenant();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const alerts = useAlerts();
  const { isStaff } = useCurrentUser();
  const [selectedChemicalIds, setSelectedChemicalIds] = useState<Row["id"][]>(
    []
  );
  const [selectedChemical, setSelectedChemical] = useState<Row | undefined>(
    undefined
  );
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState<Row[]>([]);

  useBreadcrumb([
    {
      label: "Chemicals",
      to: tenantId ? getChemicalsBreadcrumb(tenantId) : undefined,
    },
    {
      label: "Chemical Catalog",
      to: tenantId ? getChemicalsCatalogBreadcrumb(tenantId) : undefined,
    },
  ]);

  const [deleteChemical, { loading: deleting }] = useMutation(DELETE_CHEMICAL);

  const handleCloseDeleteDialog = useCallback(() => {
    setDeleteConfirmOpen(false);
    setSelectedChemical(undefined);
  }, []);

  const handleConfirmDeletion = useCallback(async () => {
    try {
      if (selectedChemical) {
        await deleteChemical({
          variables: {
            id: selectedChemical.id,
          },
          refetchQueries: ["TenantCatalogChemicals", "CatalogOverview"],
        });
        alerts.success("Successfully deleted chemical");
        handleCloseDeleteDialog();
      }
    } catch (err) {
      alerts.error("An error occurred while deleting the measurement", err);
    }
  }, [alerts, deleteChemical, selectedChemical, handleCloseDeleteDialog]);

  const handleOpenDeleteDialog = useCallback((chemical?: Row) => {
    setSelectedChemical(chemical);
    setDeleteConfirmOpen(true);
  }, []);

  const columns: OmnisearchGridColDef<Row>[] = useMemo(
    () => [
      {
        field: "name",
        headerName: "Chemical Name",
        flex: 0.5,
        renderCell: ({ row }) => (
          <TextCellWithTooltip tooltip={formatChemicalName(row)}>
            {formatChemicalName(row, {
              showCasNumber: false,
              showPureOrMixture: false,
              showMixtureDetails: false,
              showStateOfMatter: false,
              showAlternateId: false,
            })}
          </TextCellWithTooltip>
        ),
      },
      {
        field: "casNumber",
        headerName: "CAS Number",
        flex: 0.25,
        renderCell: ({ value }) => (
          <TextCellWithTooltip>{value}</TextCellWithTooltip>
        ),
      },
      {
        field: "alternateId",
        headerName: "Alternate ID",
        flex: 0.25,
        renderCell: ({ value }) => (
          <TextCellWithTooltip>{value}</TextCellWithTooltip>
        ),
      },
      {
        field: "stateOfMatter",
        headerName: "State of Matter",
        flex: 0.3,
        renderCell: ({ value }) => {
          if (!value || value.length === 0) return <></>;
          return (
            <TextCellWithTooltip>
              {value
                .map((state: string) => prettyPrintEnumValue(state))
                .join(", ")}
            </TextCellWithTooltip>
          );
        },
        filterKeyType: "stateOfMatter",
      },
      {
        field: "density",
        headerName: "Density",
        flex: 0.3,
        renderCell: ({ row }) => {
          const { density, densityUnits } = row;
          if (!density || !densityUnits) return <></>;
          return (
            <TextCellWithTooltip>
              {density} {prettyPrintEnumValue(densityUnits)}
            </TextCellWithTooltip>
          );
        },
      },
      {
        field: "EHS",
        headerName: "EHS",
        renderCell(params) {
          const isEhs = params.row.isEhs;
          if (!isEhs) return <></>;
          return <Check />;
        },
        filterKeyType: "boolean",
      },
      {
        field: "mixture",
        headerName: "Mixture",
        renderCell(params) {
          const pureOrMixture = params.row.pureOrMixture;
          if (pureOrMixture === PureOrMixture.Mixture) return <Check />;
          return <></>;
        },
        filterKeyType: "boolean",
      },
      {
        field: "SDS",
        headerName: "SDS",
        sortable: false,
        renderCell(params) {
          const hasSDS = params.row.documents
            ?.map((d) => d.document.documentType)
            .some((t) => t === DocumentType.SafetyDataSheet);
          return hasSDS ? <Check /> : <></>;
        },
        filterKeyType: "boolean",
      },
      {
        field: "needsReview",
        headerName: "Needs Review?",
        width: 110,
        renderCell: (params) => (params.row.needsReview ? <Check /> : <></>),
        filterKeyType: "boolean",
      },
      {
        field: "issueCount",
        headerName: "Issues",
        renderCell: ({ row: { issueCount } }) => (
          <IssueCount issueCount={issueCount} />
        ),
        sortable: false,
      },
      {
        field: "actions",
        type: "actions",
        getActions: ({ row }) => [
          <Tooltip title="Edit" key={1}>
            <IconButton
              href={`${window.location.pathname}/${row.id}`}
              aria-label="Edit"
              size="small"
            >
              <Edit fontSize="small" />
            </IconButton>
          </Tooltip>,
          <Tooltip title="Delete" key={2}>
            <GridActionsCellItem
              onClick={() => handleOpenDeleteDialog(row)}
              label="Delete Chemical"
              icon={<Delete />}
            />
          </Tooltip>,
        ],
      },
    ],
    [handleOpenDeleteDialog]
  );

  const commandButtons = useMemo(() => {
    const buttons = [
      <ExportDataButton
        key="export"
        exportType="chemical"
        disabled={!tenantId}
        tenantId={tenantId ?? ""}
        fileName={
          tenant?.name ? `${tenant.name}-chemical-catalog` : "chemical-catalog"
        }
      />,
      <AddChemicalButton key="add" />,
      <ChemicalCatalogBulkActions
        key="bulk-actions"
        chemicals={selectedRows}
      />,
    ];
    if (isStaff) {
      buttons.push(
        <StaffBulkActionsMenu key="staff" chemicalIds={selectedChemicalIds} />
      );
    }
    return buttons;
  }, [isStaff, selectedChemicalIds, tenantId, selectedRows, tenant]);

  return (
    <Box sx={{ py: 3 }}>
      <OmnisearchDataGrid
        persistenceKey="chemical-catalog"
        isURLDriven
        additionalFilterColumns={[
          { header: "Manufacturer/Supplier", key: "manufacturerSupplier" },
        ]}
        excludeFilterColumns={["issueCount"]}
        columns={columns}
        initialSortModel={[
          { field: "needsReview", sort: "desc" },
          { field: "name", sort: "asc" },
        ]}
        initialState={{
          columns: {
            columnVisibilityModel: {
              stateOfMatter: false,
              density: false,
            },
          },
        }}
        dataQuery={TENANT_CATALOG_CHEMICALS}
        defaultSearch={`tenantId:${tenantId}`}
        getItems={(data) => data.chemicals.items}
        getCount={(data) => data.chemicals.count}
        noDataOnButtonClick={() => navigate("new")}
        noDataMessage="No chemicals have been added to the catalog yet."
        noDataButtonText="Add Chemical"
        onSelectedIdsChanged={
          isStaff ? (ids) => setSelectedChemicalIds(ids as string[]) : undefined
        }
        onSelectedRowsChanged={setSelectedRows}
        commandButtons={commandButtons}
        getRowLink={(row) => `./${row.id}`}
        onRowClick={(params) => navigate(`./${params.row.id}`)}
        columnVisibilityModel={{
          stateOfMatter: false,
          density: false,
          ...(isMobile && {
            alternateId: false,
            EHS: false,
            mixture: false,
            SDS: false,
            needsReview: false,
            issueCount: false,
          }),
        }}
      />
      <ConfirmDialog
        loading={deleting}
        open={deleteConfirmOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDeletion}
        header="Are you sure you want to delete this chemical?"
        resources={[
          {
            resourceType: "Chemical",
            resourceName: selectedChemical?.name,
          },
        ]}
        info="This will also delete all associated inventory."
      />
    </Box>
  );
};
